/**
 * Shared time-display helpers for attendance data.
 *
 * The ZKTeco device sends wall-clock time from the device's physical location
 * (e.g., Kinshasa UTC+1). The backend stores it as a naive ISO string.
 *
 * If we parse it with `new Date()` the browser re-interprets the string based
 * on its own timezone, producing wrong displayed hours when HR/admin view the
 * app from a different timezone (e.g., Paris UTC+2).
 *
 * These helpers extract H:MM and date components DIRECTLY from the ISO string
 * without any timezone conversion, so every viewer sees the true punch time.
 */

export const formatPunchTime = (iso) => {
  if (!iso || typeof iso !== 'string') return '-';
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : iso;
};

export const formatPunchDateTime = (iso) => {
  if (!iso || typeof iso !== 'string') return '-';
  const m = iso.match(/(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!m) return iso;
  const [y, mo, d] = m[1].split('-');
  return `${d}/${mo}/${y} ${m[2]}:${m[3]}`;
};

/**
 * Return the current wall-clock time in the given IANA timezone, as "HH:MM".
 */
export const getCurrentTimeInTz = (tzName) => {
  if (!tzName) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: tzName,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date());
  } catch (e) {
    return '';
  }
};

/**
 * Return a friendly UTC offset label ("UTC+1", "UTC-5", "UTC+00:30")
 * for a given IANA timezone at the current instant.
 */
export const getUtcOffsetLabel = (tzName) => {
  if (!tzName) return '';
  try {
    const now = new Date();
    const dtf = new Intl.DateTimeFormat('en-GB', {
      timeZone: tzName,
      timeZoneName: 'shortOffset'
    });
    const parts = dtf.formatToParts(now);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    if (tzPart && tzPart.value) return tzPart.value; // e.g., "GMT+1"
  } catch (e) {
    // fallback
  }
  return '';
};
